import { Component } from 'react';
import languages from '../languages.js';
import './ArticleGenerator.scss';
import Checkbox from './Checkbox.js';
import IconPadlock from './IconPadlock.js';
import SeoHelper from '../lib/SeoHelper.js';
import LanguageInfo from './LanguageInfo.js';
import SourceField from './SourceField.js';
import LocalStorage from '../lib/LocalStorage.js';
import Markup from '../lib/Markup.js';
import Fetch from '../lib/Fetch.js';
import CheckListError from './CheckListError.js';

class ArticleGenerator extends Component {
	constructor(props) {
		super(props);
		const {appSettings} = this.props;
		this.state = {
			oldTitle: '',
			topic: '',
			introLength: 30,
			brief: '',
			tone: 'conversational',
			toneLevel: 4,
			customTone: '',
			lengthType: 'recommended',
			length: this.props.optimalValues.optimalWordCount || 1000,
			headings: '',
			headingCount: 1,
			questionCount: 5,
			questions: '',
			pointOfView: 'second person',
			options: {
				generateTitle: true,
				generateIntro: true,
				generateNlpIntro: true,
				generateFAQ: false,
				generateSummary: true,
				generateKeyPoints: true,
				generateDidYouKnow: false,
				generateCommentIncentive: false,
				paragraphsSeparated: appSettings.maxParagraphsPerHeading,
				splitToSections: true,
				keywordInH1: true,
				keywordInH2: true,
				keywordInFirstWords: 100,
				keywordInLastWords: 100,
				includeRelatedKeywords: true,
				boldForKeywords: true,
				proofread: false,
				avoidAIDetection: false,
				fixParagraphs: false,
			},
			sources: '',
			error: '',
			taskCount: 0,
			tasksDone: 0,
			failedParts: [],
			article: {},
			zerogptToken: '',
			dataSource: 'ai',
			estimatedLength: 0,
			searchResults: {},
			focusKeyword: '',
			optimalValues: {},
			relatedKeywords: [],
		}

		this.handleInputChange = this.handleInputChange.bind(this);
		this.handleFormSubmit = this.handleFormSubmit.bind(this);
		this.handleButtonClick = this.handleButtonClick.bind(this);
		this.handleOptionChange = this.handleOptionChange.bind(this);
		this.handleRetryClick = this.handleRetryClick.bind(this);
		this.saveOption = this.saveOption.bind(this);

		this.limits = {
			wordsMax: 5000,
			wordsMin: 300,
		}

	}

	async componentDidMount() {
		const {top10SearchResults, focusKeyword, relatedKeywords, optimalValues} = this.props;
		this.setState({
			searchResults: top10SearchResults,
			focusKeyword: focusKeyword,
			optimalValues: optimalValues,
			relatedKeywords: relatedKeywords,
		});

		const options = this.loadArticleOptions();
		if (options.lengthType === 'recommended' || options.lengthType === 'customWords') {
			let length = options.lengthType === 'recommended' ? this.props.optimalValues.optimalWordCount || options.length : options.length;
			this.adjustArticleLength(length);
		}
	}

	async componentDidUpdate(prevProps, prevState) {
		if (this.state.lengthType === 'recommended' && this.props.optimalValues.optimalWordCount && this.props.optimalValues.optimalWordCount !== this.state.length) {
			this.adjustArticleLength(this.props.optimalValues.optimalWordCount);
			this.setState({
				length: this.props.optimalValues.optimalWordCount,
			});
		}
		if ((this.state.lengthType === 'customWords' || this.state.lengthType === 'recommended') && this.state.length !== prevState.length) {
			this.adjustArticleLength(this.state.length);
		}
		if (prevProps.relatedKeywords !== this.props.relatedKeywords) {
			this.setState({
				relatedKeywords: this.props.relatedKeywords,
			});
		}
		if (prevProps.top10SearchResults !== this.props.top10SearchResults) {
			this.setState({
				top10SearchResults: this.props.top10SearchResults,
			});
		}
		if (prevProps.optimalValues !== this.props.optimalValues) {
			this.setState({
				optimalValues: this.props.optimalValues,
			});
		}
	}

	saveOption(name, value) {
		let saved = {};
		saved[name] = value;
		this.setState(saved);
		this.saveArticleOptions(saved);
	}

	handleInputChange(event) {
		let ignore = [
			'headingCount',
			'pointOfView',
		];
		if (!ignore.includes(event.target.id) && this.state.lengthType !== 'customSections') {
			this.adjustArticleLength(this.state.length);
		}

		let optionName = !event.target.name ? event.target.id : event.target.name;
		this.saveOption(optionName, event.target.value);
	}

	async handleButtonClick(event) {
		event.preventDefault();

		const {appSettings, focusKeyword} = this.props;
		const {dataSource} = this.state;

		if (!focusKeyword.trim()) {
			this.setState({
				error: window?.t?.ERR_NO_FOCUS_KEYWORD,
			});
			return;
		}

		let priceId = 'article-generator-' + appSettings.aiModel;
		if (dataSource !== 'ai') {
			priceId += '-data-driven';
		}

		const canProceed = await this.props.limitUsageCallback('article-generator', priceId);

		if (!canProceed) {
			this.setState({
				error: (<>{window?.t?.LIMIT_REACHED1} <a href="https://diagnoseo.com/pricing/#credits" target="_blank" rel="noopener noreferrer">{window?.t?.BUY_EXTRA_CREDITS}</a> {window?.t?.LIMIT_REACHED2}'</>),
			});
			return;
		}

		if (this.props.currentArticle) {
			if (window.confirm(window?.t?.ARTICLE_WILL_BE_LOST)) {
				this.makeArticle();
			}
		} else {
			this.makeArticle();
		}
	}

	handleFormSubmit(event) {
		event.preventDefault();
	}

	handleOptionChange(event) {
		let name = event.target.getAttribute('id');
		let options = this.state.options;

		if (name in options) {
			delete options[name];
		} else if (event.target.value) {
			options[name] = event.target.value;
		}
		this.setState({
			options: options,
		});
		if (this.state.lengthType !== 'customSections') {
			this.adjustArticleLength(this.state.length);
		}
		this.saveArticleOptions();
	}

	handleRetryClick(event) {
		event.preventDefault();
		if (!this.state.failedParts.length) {
			return;
		}
		this.generateFailedParts();
	}

	saveArticleOptions(saved) {
		if (!saved) {
			saved = {};
		}
		let brief = this.state.brief;
		if ('brief' in saved) {
			brief = saved.brief;
		}
		const settings = {
			brief: brief,
			topic: saved.topic || this.state.topic,
			introLength: saved.introLength || this.state.introLength,
			tone: saved.tone || this.state.tone,
			toneLevel: parseInt(saved.toneLevel || this.state.toneLevel),
			customTone: saved.customTone !== undefined ? saved.customTone : this.state.customTone,
			headings: saved.headings !== undefined ? saved.headings : this.state.headings,
			headingCount: parseInt(saved.headingCount || this.state.headingCount),
			questions: saved.questions || saved.questions === '' ? saved.questions : this.state.questions,
			questionCount: parseInt(saved.questionCount || this.state.questionCount),
			options: saved.options || this.state.options,
			sources: saved.sources || this.state.sources,
			dataSource: saved.dataSource || this.state.dataSource,
			lengthType: saved.lengthType || this.state.lengthType,
			length: parseInt(saved.length || this.state.length),
			pointOfView: saved.pointOfView || this.state.pointOfView,
		};
		LocalStorage.setItem('diagnoseo-article-generator-options', settings);
	}

	loadArticleOptions() {
		let settings = LocalStorage.getItem('diagnoseo-article-generator-options');
		if (!settings) {
			return this.state;
		}
		const options = {
			topic: settings.topic,
			introLength: parseInt(settings.introLength),
			tone: settings.tone || 'conversational',
			customTone: settings.customTone,
			toneLevel: parseInt(settings.toneLevel) || 4,
			headings: settings.headings || '',
			headingCount: parseInt(settings.headingCount),
			questions: settings.questions,
			questionCount: parseInt(settings.questionCount),
			options: settings.options,
			sources: settings.sources,
			dataSource: settings.dataSource || 'search',
			lengthType: settings.lengthType || 'recommended',
			length: parseInt(settings.length) || 1000,
			pointOfView: settings.pointOfView,
			brief: settings.brief || '',
		};
		this.setState(options);
		return options;
	}

	render () {
		if (!this.checkPermission('content-article-generator')) {
			return (<p className="in-pro"><IconPadlock /> {window?.t?.UNAVAILABLE_IN_PLAN} <b><a href="https://diagnoseo.com/pricing/" target="_blank" rel="noreferrer noopener">{window?.t?.UNLOCK}</a></b></p>);
		}

		if (this.state.loading) {
			let originalPageTitle = document.title.replaceAll(window?.t?.GENERATING_ARTICLE, '');
			document.title = window?.t?.GENERATING_ARTICLE + originalPageTitle;
		} else {
			let newTitle = document.title.replaceAll(window?.t?.GENERATING_ARTICLE, '');
			document.title = newTitle;
		}

		let {
			introLength,
			brief,
			tone,
			customTone,
			headings,
			headingCount,
			options,
			questionCount,
			questions,
			toneLevel,
			dataSource,
			pointOfView,
		} = this.state;
		const {focusKeyword} = this.props;

		const error = this.state.error
			? (<CheckListError text={this.state.error} />)
			: '';

		let failInfo = '';
		if (this.state.failedParts.length && !this.state.loading) {
			failInfo = this.state.failedParts.map((fail, index) => (<CheckListError text={fail.message} key={`fail${index}`} />));
			failInfo.push((<p><a href="/" onClick={this.handleRetryClick}>{window?.t?.RETRY_MISSING_PARTS}</a></p>))
		}

		let i = languages.findIndex((item) => item.langCode === this.props.language);
		let language = languages[i];

		const allowedSources = {
			'ai': true,
			'serp-summary': this.checkPermission('content-sources-google-summary'),
			'search': this.checkPermission('content-sources-google'),
			'search-en': this.checkPermission('content-sources-google'),
			'custom': this.checkPermission('content-sources-custom'),
		};

		return (
			<>
			<LanguageInfo
				language={language}
				location={this.props.appSettings.location}
				toggleSettingsCallback={this.props.toggleSettingsCallback}
			/>
			<form className="article-generator" action="#" onSubmit={this.handleFormSubmit}>
				<p>
					<label htmlFor="tone">{window?.t?.LBL_TONE_OF_VOICE}</label>
					<select name="tone" id="tone" value={tone} onChange={this.handleInputChange}>
						<option value="assertive">{window?.t?.TONE_ASSERTIVE}</option>
						<option value="authoritative">{window?.t?.TONE_AUTHORITATIVE}</option>
						<option value="cheeky">{window?.t?.TONE_CHEEKY}</option>
						<option value="clear">{window?.t?.TONE_CLEAR}</option>
						<option value="compelling">{window?.t?.TONE_COMPELLING}</option>
						<option value="concise">{window?.t?.TONE_CONCISE}</option>
						<option value="conversational">{window?.t?.TONE_CONVERSATIONAL}</option>
						<option value="courteous">{window?.t?.TONE_COURTEOUS}</option>
						<option value="emotive">{window?.t?.TONE_EMOTIVE}</option>
						<option value="empathetic">{window?.t?.TONE_EMPATHETIC}</option>
						<option value="engaging">{window?.t?.TONE_ENGAGING}</option>
						<option value="formal">{window?.t?.TONE_FORMAL}</option>
						<option value="friendly">{window?.t?.TONE_FRIENDLY}</option>
						<option value="funny">{window?.t?.TONE_FUNNY}</option>
						<option value="informative">{window?.t?.TONE_INFORMATIVE}</option>
						<option value="persuasive">{window?.t?.TONE_PERSUASIVE}</option>
						<option value="professional">{window?.t?.TONE_PROFESSIONAL}</option>
						<option value="custom">{window?.t?.TONE_CUSTOM}</option>
					</select>
				</p>
				{tone === 'custom' ? (
					<p>
						<label htmlFor="customTone">{window?.t?.LBL_CUSTOM_TONE_OF_VOICE}</label>
						<input type="text" id="customTone" value={customTone} onChange={this.handleInputChange} />
						<span className="hint">{window?.t?.HINT_TONE}</span>
					</p>
				) : ''}
				{tone === 'conversational' ? (
				<p>
					<label htmlFor="toneLevel">{window?.t?.LBL_TONE_OF_VOICE_LEVEL}</label>
					<select name="toneLevel" id="toneLevel" value={toneLevel} onChange={this.handleInputChange}>
						<option value="1">1</option>
						<option value="2">2</option>
						<option value="3">3</option>
						<option value="4">4</option>
						<option value="5">5</option>
					</select>
				</p>
				) : ''}
				<p>
					<label htmlFor="lengthType">{window?.t?.LBL_ARTICLE_LENGTH}</label>
					<select value={this.state.lengthType} id="lengthType" name="lengthType" onChange={this.handleInputChange}>
						<option value="recommended">{window?.t?.LENGTH_RECOMMENDED}</option>
						<option value="customWords">{window?.t?.LENGTH_WORDS}</option>
						<option value="customSections">{window?.t?.LENGTH_SUBHEADINGS}</option>
					</select>
				</p>
				{this.state.lengthType === 'customSections'
					? <p>
					<label htmlFor="headingCount">{window?.t?.LBL_NUMBER_OF_SUBHEADINGS}</label>
					<input type="number" min="1" max="20" id="headingCount" value={headingCount} onChange={this.handleInputChange} />
					<span className="hint">{window?.t?.HINT_NUMBER_SUBHEADINGS}</span>
				</p> : ''}
				{this.state.lengthType === 'customWords'
					? <p>
					<label htmlFor="length">{window?.t?.LBL_NUMBER_OF_WORDS}</label>
					<input type="number" min={this.limits.wordsMin} max={this.limits.wordsMax} id="length" name="length" value={this.state.length} onChange={this.handleInputChange} />
					<span className="hint">{window?.t?.HINT_WORDS_BETWEEN} {this.limits.wordsMin} {window?.t?.AND} {this.limits.wordsMax}</span>
				</p> : ''}
				{this.state.lengthType === 'recommended'
					? <p><button className="button secondary" onClick={this.props.getOptimalData}>{window?.t?.LOAD_RECOMMENDED_LENGTH}</button></p>
					: ''
				}
				<p className="hint">{window?.t?.P_PER_SECTION} <b>{this.props.appSettings.maxParagraphsPerHeading}</b><br/>{window?.t?.WORDS_PER_P} <b>{this.props.appSettings.maxWordsPerParagraph}</b><br/>{window?.t?.OPTIMAL_LENGTH} <b>{this.props.optimalValues.optimalWordCount ? `${parseInt(this.props.optimalValues.optimalWordCount)} ${window?.t?.WORDS}` : window?.t?.UNKNOWN}</b></p>
				<p className="hint">{window?.t?.NUMBER_OF_SECTIONS} <b>{this.state.headingCount}</b><br/>{window?.t?.ESTIMATED_LENGTH} <b>{parseInt(this.estimateArticleLength())} {window?.t?.WORDS}</b></p>
				<p>
					<label htmlFor="headings">{window?.t?.LBL_SUBHEADINGS_TO_USE}</label>
					<textarea id="headings" rows="4" value={headings} onChange={this.handleInputChange}></textarea>
					<span className="hint">{window?.t?.HINT_SUBHEADINGS_TO_USE1} <a href="/tools/article-outline-generator">{window?.t?.OUTLINE_GENERATOR_LINK_TEXT}</a>. {window?.t?.HINT_SUBHEADINGS_TO_USE2}</span>
				</p>
				<p>
					<label htmlFor="brief">{window?.t?.LBL_CONTENT_DETAILS}</label>
					<textarea id="brief" rows="4" value={brief} onChange={this.handleInputChange}></textarea>
					<span className="hint">{window?.t?.HINT_ADDITIONAL_INTRUCTIONS}</span>
				</p>
				<ul className="options">
					<li>
						<Checkbox
							value={true}
							name="includeRelatedKeywords"
							checked={options.includeRelatedKeywords}
							label={window?.t?.CHK_INCLUDE_RELATED_KEYWORDS}
							onChange={this.handleOptionChange}
						/>
					</li>
					<li>
						<Checkbox
							value={true}
							name="generateTitle"
							checked={options.generateTitle}
							label={window?.t?.CHK_GENERATE_TITLE}
							onChange={this.handleOptionChange}
							extraTokens={true}
						/>
					</li>
					<li>
						<Checkbox
							value={true}
							name="generateIntro"
							checked={options.generateIntro}
							label={window?.t?.CHK_GENERATE_INTRO}
							onChange={this.handleOptionChange}
							extraTokens={true}
						/>
						{options.generateIntro
						? (<>
						<p>
							<label htmlFor="introLength">{window?.t?.LBL_INTRODUCTION_LENGTH}</label>
							<input type="number" max={this.props.appSettings.maxWordsInIntro || 100} id="introLength" value={introLength} onChange={this.handleInputChange} />
							<span className="hint">{window?.t?.HINT_INTRO_LENGTH}</span>
						</p>
						<Checkbox
							value={true}
							name="generateNlpIntro"
							checked={options.generateNlpIntro}
							label={window?.t?.CHK_GENERATE_NLP_INTRO}
							onChange={this.handleOptionChange}
						/>
						</>)
						: ''
						}
					</li>
					<li>
						<Checkbox
							value={true}
							name="generateSummary"
							checked={options.generateSummary}
							label={window?.t?.CHK_GENERATE_SUMMARY}
							onChange={this.handleOptionChange}
							extraTokens={true}
						/>
					</li>
					<li>
						<Checkbox
							value={true}
							name="generateKeyPoints"
							checked={options.generateKeyPoints}
							label={window?.t?.CHK_GENERATE_KEY_POINTS}
							onChange={this.handleOptionChange}
							extraTokens={true}
						/>
					</li>
					<li>
						<Checkbox
							value={true}
							name="generateDidYouKnow"
							checked={options.generateDidYouKnow}
							label={window?.t?.CHK_GENERATE_DID_YOU_KNOW}
							onChange={this.handleOptionChange}
							extraTokens={true}
						/>
					</li>
					<li>
						<Checkbox
							value={true}
							name="generateFAQ"
							checked={options.generateFAQ}
							label={window?.t?.CHK_GENERATE_FAQ}
							onChange={this.handleOptionChange}
							extraTokens={true}
						/>
						{options.generateFAQ ? (
						<p>
							<label htmlFor="questionCount">{window?.t?.LBL_NUMBER_OF_QUESTION_ANSWER_PAIRS}</label>
							<input type="number" min="1" max="20" id="questionCount" value={questionCount} onChange={this.handleInputChange} />
							<span className="hint">{window?.t?.HINT_FAQ_LENGTH}</span>
						</p>
						) : ''
						}
						{options.generateFAQ ? (
						<p>
							<label htmlFor="questions">{window?.t?.LBL_QUESTIONS}</label>
							<textarea id="questions" rows="4" value={questions} onChange={this.handleInputChange}></textarea>
							<span className="hint">{window?.t?.HINT_QUESTIONS}</span>
						</p>
						) : ''
						}
					</li>
					<li>
						<Checkbox
							value={true}
							name="generateCommentIncentive"
							checked={options.generateCommentIncentive}
							label={window?.t?.CHK_GENERATE_COMMENT_INCENTIVE}
							onChange={this.handleOptionChange}
							extraTokens={true}
						/>
					</li>
					<li>
						<Checkbox
							value={focusKeyword}
							name="keywordInH1"
							checked={options.keywordInH1}
							label={window?.t?.CHK_KEYWORD_IN_H1}
							onChange={this.handleOptionChange}
						/>
					</li>
					<li>
						<Checkbox
							value={focusKeyword}
							name="keywordInH2"
							checked={options.keywordInH2}
							label={window?.t?.CHK_KEYWORD_IN_H2}
							onChange={this.handleOptionChange}
						/>
					</li>
					<li>
						<Checkbox
							value={focusKeyword}
							name="keywordInFirstWords"
							checked={options.keywordInFirstWords}
							label={window?.t?.CHK_KEYWORD_IN_FIRST_WORDS}
							onChange={this.handleOptionChange}
						/>
					</li>
					<li>
						<Checkbox
							value={focusKeyword}
							name="keywordInLastWords"
							checked={options.keywordInLastWords}
							label={window?.t?.CHK_KEYWORD_IN_LAST_WORDS}
							onChange={this.handleOptionChange}
						/>
					</li>
					<li>
						<Checkbox
							value={true}
							name="boldForKeywords"
							checked={options.boldForKeywords}
							label={window?.t?.CHK_BOLD_FOR_KEYWORDS}
							onChange={this.handleOptionChange}
						/>
					</li>
					<li>
						<label>{window?.t?.LBL_WRITE_BASED_ON}:</label>
						<ul className="radio-set">
							<li>
								<input type="radio" checked={dataSource === 'ai'} value="ai" name="dataSource" id="data-source-ai" onChange={this.handleInputChange} />
								<label htmlFor="data-source-ai">{window?.t?.LBL_SRC_AI}</label>
							</li>
							<li>
								<input type="radio" checked={dataSource === 'serp-summary'} disabled={!allowedSources['serp-summary']} value="serp-summary" name="dataSource" id="data-source-serp-summary" onChange={this.handleInputChange} />
								<label htmlFor="data-source-serp-summary">{window?.t?.LBL_SRC_KNOWLEDGE_GRAPH} {!allowedSources['serp-summary'] ? (<span className="hint">{window?.t?.AVAILABLE_IN_ADVANCED}</span>) : null}</label>
							</li>
							{allowedSources['search'] ? (
							<li>
								<input type="radio" checked={dataSource === 'search'} disabled={!allowedSources['search']} value="search" name="dataSource" id="data-source-search" onChange={this.handleInputChange} />
								<label htmlFor="data-source-search">{window?.t?.LBL_SRC_TOP10}</label>
							</li>
							) : ''}
							{allowedSources['search-en'] ? (
							<li>
								<input type="radio" checked={dataSource === 'search-en'} value="search-en" name="dataSource" id="data-source-search-en" onChange={this.handleInputChange} />
								<label htmlFor="data-source-search-en">{window?.t?.LBL_SRC_TOP10_EN}</label>
							</li>
							) : ''}
							<li>
								<input type="radio" checked={dataSource === 'custom'} disabled={!allowedSources['custom']} value="custom" name="dataSource" id="data-source-custom" onChange={this.handleInputChange} />
								<label htmlFor="data-source-custom">{window?.t?.LBL_CUSTOM} {!allowedSources['custom'] ? (<span className="hint">{window?.t?.AVAILABLE_IN_ADVANCED}</span>) : null}</label>
							</li>
						</ul>

						{dataSource === 'custom'
							? <SourceField
								changeCallback={this.saveOption}
								value={this.state.sources}
								language={this.getCurrentLanguageName()}
								topic={this.state.topic}
								keyword={this.props.focusKeyword}
							/>
							: ''
						}
					</li>
					<li>
						<label htmlFor="pointOfView">{window?.t?.LBL_POINT_OF_VIEW}</label>
						<select value={pointOfView} name="pointOfView" id="pointOfView" onChange={this.handleInputChange}>
							<option value="first person singular">{window?.t?.POV_FIRST_SIN}</option>
							<option value="first person plural">{window?.t?.POV_FIRST_PLU}</option>
							<option value="second person">{window?.t?.POV_SECOND}</option>
							<option value="third person singular">{window?.t?.POV_THIRD_SIN}</option>
							<option value="third person plural">{window?.t?.POV_THIRD_PLU}</option>
						</select>
					</li>
					{this.checkPermission('content-fix-paragraph') ? (
					<li>
						<Checkbox
							value={true}
							name="fixParagraphs"
							checked={options.fixParagraphs}
							label={window?.t?.CHK_FIX_PARAGRAPHS}
							onChange={this.handleOptionChange}
							extraTokens={true}
						/>
					</li>
					) : ''
					}
					{this.checkPermission('content-proofread') ? (
					<li>
						<Checkbox
							value={true}
							name="proofread"
							checked={options.proofread}
							label={window?.t?.CHK_PROOFREAD}
							onChange={this.handleOptionChange}
							extraTokens={true}
						/>
					</li>
					) : ''}
					{this.checkPermission('content-prevent-ai-detection') ? (
					<li>
						<Checkbox
							value={true}
							name="avoidAIDetection"
							checked={options.avoidAIDetection}
							label={window?.t?.CHK_AVOID_AI_DETECTION}
							onChange={this.handleOptionChange}
							extraTokens={true}
						/>
					</li>
					) : ''
					}
				</ul>
				{error}
				{failInfo}
				<p className="main-button"><button className="button primary" onClick={this.handleButtonClick}>{window?.t?.GENERATE_ARTICLE}</button></p>
			</form>
			</>
		)
	}

	async makeArticle() {
		let {topic, headings, options, headingCount, dataSource, relatedKeywords, optimalValues, top10SearchResults} = this.state;

		let {language, passInfo, focusKeyword} = this.props;
		this.setState({error: ''});
		headingCount = parseInt(headingCount);
		this.props.checkKeyword();

		if (!topic && !focusKeyword) {
			this.setState({
				error: window?.t?.ERR_NO_FOCUS_KEYWORD,
			});
			passInfo({
				loading: false,
				loaderType: '',
			})
			return;
		}

		let estimatedLength = this.estimateArticleLength();
		if (estimatedLength > this.limits.wordsMax) {
			let msg = window?.t?.ERR_ESTIMATED_TOO_LONG.replace('[estimatedLength]', estimatedLength).replace('[maxWords]', this.limits.wordsMax);
			alert(msg);
			this.setState({
				tasksDone: 0,
				taskCount: 0,
			})
			passInfo({
				loading: false,
				loadingTitle: '',
				loadingInfo: '',
				loadingProgress: 0,
				loaderType: '',
			});
			return;
		}

		let needNewOptimalValues = !optimalValues || optimalValues.language !== language || optimalValues.keyword !== focusKeyword;
		let needNewSearchResults = (!top10SearchResults || !top10SearchResults.length || top10SearchResults.keyword !== focusKeyword) && dataSource !== 'ai' && dataSource !== 'custom';
		let needNewRelatedKeywords = options.includeRelatedKeywords && !relatedKeywords.length;

		if (needNewRelatedKeywords || needNewSearchResults || needNewOptimalValues) {
			let data = await this.props.loadKeywordsAndOptimalValues();
			if (data) {
				needNewOptimalValues = false;
				needNewSearchResults = false;
				needNewRelatedKeywords = false;
				this.setState({
					relatedKeywords: data.relatedKeywords,
					optimalValues: data.optimalValues,
					top10SearchResults: data.searchResults,
				});

				relatedKeywords = data.relatedKeywords;
				optimalValues = data.optimalValues;
				top10SearchResults = data.searchResults;
			}
		}

		if (this.state.lengthType === 'recommended' && needNewOptimalValues) {
			optimalValues = await this.props.getOptimalData();
			if (optimalValues.optimalWordCount) {
				headingCount = this.adjustArticleLength(optimalValues.optimalWordCount);
			}
		}

		var mustGetSearchResults = false;
		var hasSearchResultMarkup = top10SearchResults && top10SearchResults.markup;

		if (
			dataSource === 'search-en' ||
			((dataSource === 'serp-summary' || dataSource === 'search') && (!hasSearchResultMarkup || top10SearchResults.keyword !== focusKeyword))
		) {
			mustGetSearchResults = true;
		}

		// setting task count for the progressbar
		let taskCount = 0;
		if (dataSource === 'serp-summary') {
			taskCount = mustGetSearchResults
				? 3
				: hasSearchResultMarkup
					? 1
					: 2;
		} else if (dataSource === 'search' || dataSource === 'search-en') {
			taskCount = mustGetSearchResults
				? 2
				: hasSearchResultMarkup
					? 0
					: 1;
		} else if (dataSource === 'custom') {
			taskCount = 1;
		}

		taskCount ++;

		if (this.state.options.includeRelatedKeywords && !relatedKeywords.length) {
			taskCount ++;
		}
		if (!headings) {
			taskCount ++;
		}
		if (options.generateIntro) {
			taskCount ++;
		}
		if (options.generateTitle) {
			taskCount ++;
		}
		if (options.generateFAQ) {
			taskCount ++;
		}
		if (options.generateSummary) {
			taskCount ++;
		}
		if (options.generateKeyPoints) {
			taskCount ++;
		}
		if (options.generateDidYouKnow) {
			taskCount ++;
		}
		if (options.generateCommentIncentive) {
			taskCount ++;
		}
		if (options.avoidAIDetection) {
			taskCount ++;
		}

		this.setState({
			taskCount: taskCount,
		});
		passInfo({
			loading: true,
			loadingTitle: window?.t?.WAIT,
			loadingTitleInfo: window?.t?.DONT_CLOSE_WINDOW,
			loadingInfo: window?.t?.GENERATING_ARTICLE_INFO,
			loaderType: 'progress',
			loadingProgress: 0,
			loadingData: {
				focusKeyword: focusKeyword,
				language: this.getCurrentLanguageNameTranslation(),
			},
		})

		this.generateArticle(headingCount);
	}
	adjustArticleLength(targetLength) {
		let {headingCount} = this.state;
		let {appSettings} = this.props;
		headingCount = parseInt(headingCount);
		if (isNaN(headingCount)) {
			headingCount = 1;
		}

		let sectionLength = 0.6 * (appSettings.maxParagraphsPerHeading * appSettings.maxWordsPerSentence * appSettings.maxSentencesPerParagraph + 10);
		let estLength = this.estimateArticleLength();

		if (targetLength) {
			let minLength = targetLength * 0.9;
			let maxLength = targetLength * 1.2;
			if (estLength < minLength) {
				while (estLength < minLength) {
					headingCount ++;
					estLength += sectionLength;
				}
			} else if (estLength > maxLength) {
				while (estLength > maxLength) {
					headingCount --;
					estLength -= sectionLength;
				}
			}
		}

		if (this.state.headingCount !== headingCount) {
			this.setState({
				headingCount: headingCount,
			});
		}

		return headingCount;
	}

	async generateArticle(headingCount) {
		const {passInfo} = this.props;
		this.setState({
			article: {},
			failedParts: [],
		});

		let {topic, headings, options, sources, dataSource, relatedKeywords, top10SearchResults} = this.state;
		let {appSettings, focusKeyword} = this.props;

		if (!focusKeyword && !topic) {
			this.setState({
				error: window?.t?.ERR_NO_FOCUS_KEYWORD,
			});
			return;
		}

		let apiKeyOK = await this.checkOpenAiKey();
		if (!apiKeyOK) {
			this.setState({
				error: window?.t?.ERR_NO_API_KEY,
			});
			passInfo({
				loading: false,
				loadingTitle: '',
				loadingInfo: '',
				loadingProgress: 0,
				loaderType: '',
			});
			return;
		}

		var mustGetSearchResults = false;
		var mustGetPageSource = false;
		var hasSearchResultMarkup = top10SearchResults && top10SearchResults.markup && top10SearchResults.markup.length;

		if (
			dataSource === 'search-en' ||
			((dataSource === 'serp-summary' || dataSource === 'search') && (!hasSearchResultMarkup || top10SearchResults.keyword !== focusKeyword))
		) {
			mustGetSearchResults = true;
			mustGetPageSource = true;
		}

		if (dataSource === 'custom') {
			mustGetPageSource = true;
		}

		let estimatedLength = this.estimateArticleLength();
		if (estimatedLength > this.limits.wordsMax) {
			let msg = window?.t?.ERR_ESTIMATED_TOO_LONG.replace('[estimatedLength]', estimatedLength).replace('[maxWords]', this.limits.wordsMax);
			alert(msg);
			this.setState({
				loaderType: '',
				tasksDone: 0,
				taskCount: 0,
			});
			passInfo({
				loading: false,
				loadingTitle: '',
				loadingInfo: '',
				loadingProgress: 0,
			});
			return;
		}

		relatedKeywords = relatedKeywords.length ? relatedKeywords.map((word) => word.name) : [];

		var sourceContent = [];
		if (dataSource !== 'custom') {
			sources = [];
			this.setState({sources: ''});
		}

		if (dataSource !== 'ai') {
			var src = dataSource === 'custom' && sources ? sources.split("\n") : [];
			if (src.length > 10) {
				src = src.splice(0, 10);
			}

			if (mustGetSearchResults) {
				passInfo({
					loading: true,
					loadingTitle: window?.t?.WAIT,
					loadingTitleInfo: window?.t?.DONT_CLOSE_WINDOW,
					loadingInfo: window?.t?.FETCHING_SR,
				});

				let searchFor = topic || focusKeyword;
				if (dataSource === 'search-en') {
					const translated = await SeoHelper.translate(searchFor, appSettings.aiModel);
					if (translated) {
						searchFor = translated;
					}
				}

				const searchResultUrls = await SeoHelper.getTop10SearchResults(searchFor, dataSource === 'search-en' ? 'English' : this.getCurrentLanguageName());
				if (searchResultUrls.length) {
					searchResultUrls.forEach((url) => src.push(url));
				} else {
					dataSource = 'ai';
					mustGetPageSource = false;
					this.setState({
						dataSource: dataSource,
					});
				}
				this.incTasksDone();
			}

			if (mustGetPageSource) {
				const uniqueSrc = src.filter((value, index, array) => array.indexOf(value) === index);
				for (let i = 0; i < uniqueSrc.length; i ++) {
					passInfo({
						loadingInfo: window?.t?.FETCHING_CONTENTS_OF + ' ' + uniqueSrc[i],
					});
					let pageContent = await SeoHelper.getPageMarkup(uniqueSrc[i]);
					if (pageContent.status === 'ok' && pageContent.body) {
						sourceContent.push(Markup.cleanContent(pageContent.body));
					}
				}
				if (!sourceContent.length) {
					dataSource = 'ai';
					this.setState({
						dataSource: dataSource,
					});
				}
				this.incTasksDone();
				this.setState({
					sourceContent: sourceContent,
				});
			} else {
				if (top10SearchResults?.markup && top10SearchResults?.markup.length) {
					sourceContent = top10SearchResults.markup.map((html) => Markup.cleanContent(html));
				}
				if (sourceContent.length) {
					this.setState({
						sourceContent: sourceContent,
					});
				} else {
					dataSource = 'ai';
					this.setState({
						dataSource: dataSource,
					});
				}
			}

			if (dataSource === 'serp-summary') {
				passInfo({
					loadingInfo: window?.t?.CREATING_KNOWLEDGE_GRAPH,
				});
				let hasSourceContent = false;
				for (let i = 0; i < sourceContent.length; i ++) {
					sourceContent[i] = await this.summarizePageContent(sourceContent[i]);
					if (sourceContent[i]) {
						hasSourceContent = true;
					}
				}

				if (!hasSourceContent) {
					dataSource = 'ai';
					this.setState({
						dataSource: dataSource,
					});
				}
				this.incTasksDone();
			}
		}

		/* article generation starts here */

		const article = {
			sectionTitles: [],
			sectionTexts: [],
		};

		passInfo({
			loading: window?.t?.PREPARING_SECTION_TITLES,
		});
		if (headings) {
			article.sectionTitles = headings.split("\n").map((title) => {
				return {
					level: 1,
					text: title.trim(),
				};
			});
		} else {
			let outline = await this.generateArticleOutline(topic || focusKeyword, headingCount, sourceContent);
			if (outline.status === 'ok') {
				article.sectionTitles = outline.outline;
			} else {
				const failedPart = {
					type: 'outline',
					message: outline.message && outline.message.includes('Incorrect API key provided') ? window?.t?.ERR_GENERATING_OUTLINE_API_KEY : window?.t?.ERR_GENERATING_OUTLINE,
				}
				const failed = this.state.failedParts;
				failed.push(failedPart);
				passInfo({
					loading: false,
					loadingTitle: '',
					loadingInfo: '',
					loadingProgress: 0,
					loaderType: '',
				});
				this.setState({
					failedParts: failed,
					taskCount: 0,
					tasksDone: 0,
				});
				return;
			}
		}
		this.incTasksDone();

		// section texts
		const usedKeywords = [];

		passInfo({
			loadingInfo: window?.t?.GENERATING_ARTICLE_SECTIONS,
		});
		const sectionContent = await this.generateArticleSections(article.sectionTitles.map((item) => item.text), sourceContent);

		if (sectionContent.length) {
			for (let i = 0; i < article.sectionTitles.length; i ++) {
				let index = sectionContent.findIndex((section) => section.sectionTitle === article.sectionTitles[i].text);
				if (index > -1) {
					article.sectionTexts[i] = sectionContent[index].paragraphs;
					if (this.state.options.includeRelatedKeywords) {
						relatedKeywords = relatedKeywords.filter((word) => {
							for (var j = 0; j < sectionContent[index].paragraphs.length; j ++) {
								if (sectionContent[index].paragraphs[j] && sectionContent[index].paragraphs[j].includes(word)) {
									usedKeywords.push(word);
									return false;
								}
							}
							return true;
						});
					}
					if (options.fixParagraphs) {
						passInfo({
							loading: `${window?.t?.FIXING_CONTENT_FOR_SECTION}: ${article.sectionTitles[i].text}...`,
						});
						let pCount = article.sectionTexts[i].length;
						for (let j = 0; j < pCount; j ++) {
							article.sectionTexts[i][j] = await this.fixParagraph(article.sectionTexts[i][j]);
						}
					}
					if (options.proofread) {
						passInfo({
							loading: `${window?.t?.PROOFREADING_SECTION}: ${article.sectionTitles[i].text}...`,
						});
						let pCount = article.sectionTexts[i].length;
						for (let j = 0; j < pCount; j ++) {
							article.sectionTexts[i][j] = await this.proofread(article.sectionTexts[i][j]);
						}
					}
				}
			}
		} else {
			article.sectionTexts = [];
			const failedPart = {
				type: 'sections',
				message: window?.t?.ERR_GENERATING_SECTION_CONTENT,
			}
			const failed = this.state.failedParts;
			failed.push(failedPart);
			this.setState({
				failedParts: failed,
			});
		}
		this.incTasksDone();

		// intro
		if (options.generateIntro) {
			passInfo({
				loadingInfo: window?.t?.GENERATING_INTRO,
			});
			let intro = await this.generateArticleIntro(article.sectionTitles, article.sectionTexts);
			if (intro.status === 'ok') {
				article.intro = intro.text;
				if (options.fixParagraphs) {
					article.intro = await this.fixParagraph(article.intro);
				}
				if (options.proofread) {
					passInfo({
						loadingInfo: window?.t?.PROOFREADING_INTRO,
					});
					article.intro = await this.proofread(article.intro);
				}
			} else {
				const failedPart = {
					type: 'intro',
					message: window?.t?.ERR_GENERATING_INTRO,
				}
				const failed = this.state.failedParts;
				failed.push(failedPart);
				this.setState({
					failedParts: failed,
				});
			}
			this.incTasksDone();
		}

		// title
		if (options.generateTitle) {
			passInfo({
				loadingInfo: window?.t?.GENERATING_HEADLINE,
			});
			let title = await this.generateArticleTitle(article.sectionTitles);
			if (title.status === 'ok') {
				article.title = title.text;
				if (options.proofread) {
					passInfo({
						loadingInfo: window?.t?.PROOFREADING_HEADLINE,
					});
					article.title = await this.proofread(article.title);
				}
			} else {
				const failedPart = {
					type: 'title',
					message: title.message && title.message.includes('Incorrect API key provided') ? window?.t?.ERR_GENERATING_HEADLINE_API_KEY : window?.t?.ERR_GENERATING_HEADLINE,
				}
				const failed = this.state.failedParts;
				failed.push(failedPart);
				this.setState({
					failedParts: failed,
				});
			}
			this.incTasksDone();
		}

		// summary
		if (options.generateSummary) {
			passInfo({
				loadingInfo: window?.t?.GENERATING_SUMMARY,
			});
			let summary = await this.generateArticleSummary(article);
			if (summary.status === 'ok') {
				article.summaryTitle = summary.title;
				article.summary = summary.text;
				if (options.fixParagraphs) {
					article.summary = await this.fixParagraph(article.summary);
				}
				if (options.proofread) {
					passInfo({
						loadingInfo: window?.t?.PROOFREADING_SUMMARY,
					});
					article.summary = await this.proofread(article.summary);
				}
			} else {
				const failedPart = {
					type: 'summary',
					message: summary.message && summary.message.includes('Incorrect API key provided') ? window?.t?.ERR_GENERATING_SUMMARY_API_KEY : window?.t?.ERR_GENERATING_SUMMARY,
				}
				const failed = this.state.failedParts;
				failed.push(failedPart);
				this.setState({
					failedParts: failed,
				});
			}
			this.incTasksDone();
		}

		// key points
		if (options.generateKeyPoints) {
			passInfo({
				loadingInfo: window?.t?.GENERATING_KEY_POINTS,
			});
			let keyPoints = await this.generateKeyPoints(article);
			if (keyPoints.status === 'ok') {
				article.keyPoints = keyPoints.points.filter((point) => {
					let text = Markup.stripTags(point).trim();
					return !!text;
				});
				if (options.proofread) {
					passInfo({
						loadingInfo: window?.t?.PROOFREADING_KEY_POINTS,
					});
					for (let i = 0; i < article.keyPoints.length; i ++) {
						article.keyPoints[i] = await this.proofread(article.keyPoints[i]);
					}
				}
			} else {
				const failedPart = {
					type: 'keyPoints',
					message: keyPoints.message && keyPoints.message.includes('Incorrect API key provided') ? window?.t?.ERR_GENERATING_KEY_POINTS_API_KEY : window?.t?.ERR_GENERATING_KEY_POINTS,
				}
				const failed = this.state.failedParts;
				failed.push(failedPart);
				this.setState({
					failedParts: failed,
				});
			}
			this.incTasksDone();
		}

		// did you know
		if (options.generateDidYouKnow) {
			passInfo({
				loadingInfo: window?.t?.GENERATING_DID_YOU_KNOW,
			});
			let didYouKnow = await this.generateDidYouKnow(article, sourceContent);
			if (didYouKnow.status === 'ok') {
				article.didYouKnow = didYouKnow.content;
				if (options.fixParagraphs) {
					article.didYouKnow.text = await this.fixParagraph(article.didYouKnow.text);
				}
				if (options.proofread) {
					passInfo({
						loadingInfo: window?.t?.PROOFREADING_DID_YOU_KNOW,
					});
					article.didYouKnow.title = await this.proofread(article.didYouKnow.title);
					article.didYouKnow.text = await this.proofread(article.didYouKnow.text);
				}
			} else {
				const failedPart = {
					type: 'didYouKnow',
					message: didYouKnow.message && didYouKnow.message.includes('Incorrect API key provided') ? window?.t?.ERR_GENERATING_DID_YOU_KNOW_API_KEY : window?.t?.ERR_GENERATING_DID_YOU_KNOW,
				}
				const failed = this.state.failedParts;
				failed.push(failedPart);
				this.setState({
					failedParts: failed,
				});
			}
			this.incTasksDone();
		}

		// faq
		if (options.generateFAQ) {
			passInfo({
				loadingInfo: window?.t?.GENERATING_FAQ,
			});
			let faq = await this.generateArticleFAQ(sourceContent);
			if (faq.status === 'ok') {
				article.faqTitle = faq.title;
				article.faqQuestions = faq.questions;
				if (options.fixParagraphs) {
					for (let i = 0; i < article.faqQuestions.length; i ++) {
						article.faqQuestions[i].a = await this.fixParagraph(article.faqQuestions[i].a);
					}
				}
				if (options.proofread) {
					passInfo({
						loadingInfo: window?.t?.PROOFREADING_FAQ,
					});
					for (let i = 0; i < article.faqQuestions.length; i ++) {
						article.faqQuestions[i].a = await this.proofread(article.faqQuestions[i].a);
					}
				}
			} else {
				const failedPart = {
					type: 'faq',
					message: faq.message && faq.message.includes('Incorrect API key provided') ? window?.t?.ERR_GENERATING_FAQ_API_KEY : window?.t?.ERR_GENERATING_FAQ,
				}
				const failed = this.state.failedParts;
				failed.push(failedPart);
				this.setState({
					failedParts: failed,
				});
			}
			this.incTasksDone();
		}

		// comment incentive
		if (options.generateCommentIncentive) {
			passInfo({
				loadingInfo: window?.t?.GENERATING_COMMENT_INCENTIVE,
			});
			let commentInc = await this.generateCommentIncentive(article);
			if (commentInc.status === 'ok') {
				article.commentIncentive = commentInc.text;
				if (options.fixParagraphs) {
					article.commentIncentive = await this.fixParagraph(article.commentIncentive);
				}
				if (options.proofread) {
					passInfo({
						loadingInfo: window?.t?.PROOFREADING_COMMENT_INCENTIVE,
					});
					article.commentIncentive = await this.proofread(article.commentIncentive);
				}
			} else {
				const failedPart = {
					type: 'commentIncentive',
					message: commentInc.message && commentInc.message.includes('Incorrect API key provided') ? window?.t?.ERR_GENERATING_COMMENT_INCENTIVE_API_KEY : window?.t?.ERR_GENERATING_COMMENT_INCENTIVE,
				}
				const failed = this.state.failedParts;
				failed.push(failedPart);
				this.setState({
					failedParts: failed,
				});
			}
			this.incTasksDone();
		}

		// AI detection and humanization
		if (options.avoidAIDetection) {
			passInfo({
				loadingInfo: window?.t?.HUMANIZING,
			});

			for (let part in article) {
				const excludedParts = ['faqTitle', 'sectionTitles', 'keyPoints', 'summaryTitle', 'title'];
				if (excludedParts.includes(part)) {
					continue;
				}
				switch (part) {
					case 'sectionTexts':
						for (let s = 0; s < article.sectionTexts.length; s ++) {
							if (!article.sectionTexts[s].length) {
								continue;
							}

							let text = article.sectionTexts[s].join(' ');
							text = Markup.stripTags(text);
							let humanized = await this.avoidAIDetection(text, true);

							if (humanized) {
								for (let i = 0; i < humanized.length; i ++) {
									humanized[i] = await this.fixParagraph(humanized[i]);
									if (options.proofread) {
										humanized[i] = await this.proofread(humanized[i]);
									}
								}
								article.sectionTexts[s] = humanized;
							}
						}
						break;
					case 'faqQuestions':
						for (let i = 0; i < article.faqQuestions.length; i ++) {
							article.faqQuestions[i].a = await this.avoidAIDetection(article.faqQuestions[i].a);
							article.faqQuestions[i].a = await this.fixParagraph(article.faqQuestions[i].a);
							if (options.proofread) {
								article.faqQuestions[i].a = await this.proofread(article.faqQuestions[i].a);
							}
						}
						break;
					case 'didYouKnow':
						article.didYouKnow.text = await this.avoidAIDetection(article.didYouKnow.text);
						article.didYouKnow.text = await this.fixParagraph(article.didYouKnow.text);
						if (options.proofread) {
							article.didYouKnow.text = await this.proofread(article.didYouKnow.text);
						}
						break;
					case 'intro':
						article.intro = await this.avoidAIDetection(article.intro);
						article.intro = await this.fixParagraph(article.intro);
						if (options.proofread) {
							article.intro = await this.proofread(article.intro);
						}
						break;
					case 'summary':
						article.summary = await this.avoidAIDetection(article.summary);
						article.summary = await this.fixParagraph(article.summary);
						if (options.proofread) {
							article.summary = await this.proofread(article.summary);
						}
						break;
					default:
						article[part] = await this.avoidAIDetection(article[part]);
						if (options.proofread) {
							article[part] = await this.proofread(article[part]);
						}
				}
			}
			this.incTasksDone();
		}

		this.setState({
			article: article,
		});
		passInfo({
			loadingInfo: window?.t?.BUILDING_MARKUP,
		});
		let articleMarkup = this.buildArticleMarkup(article);
		this.setState({
			tasksDone: 0,
			taskCount: 0,
		});
		passInfo({
			loading: false,
			loadingTitle: '',
			loadingInfo: '',
			loadingProgress: 0,
			loaderType: '',
		})
		this.props.contentCallback(articleMarkup);
	}

	async summarizePageContent(markup) {
		const {appSettings} = this.props;

		const result = await Fetch.post(`https://${window.location.hostname}/tools/content/ajax/summarize-page.php`, {
			text: Markup.stripTags(markup),
			language: this.getCurrentLanguageName(),
			aiModel: appSettings.aiModel,
		});
		return result.status === 'ok' ? result.summary : '';
	}

	async generateArticleOutline(subject, sectionCount, sources = []) {
		const {dataSource, brief} = this.state;
		const {appSettings, relatedKeywords} = this.props;
		const postData = {
			subject: subject,
			headingCount: sectionCount || 5,
			language: this.getCurrentLanguageName(),
			aiModel: appSettings.outlineAiModel,
		};

		if (relatedKeywords.length) {
			postData.relatedKeywords = relatedKeywords.map((kw) => kw.name).join(', ');
		}

		if (appSettings.disallowedWords) {
			let {disallowedWords} = appSettings;
			disallowedWords = disallowedWords.replaceAll("\n", ", ");
			if (disallowedWords) {
				postData.forbiddenWords = disallowedWords;
			}
		}
		if ((dataSource === 'search' || dataSource === 'search-en' || dataSource === 'custom') && sources.length) {
			const titles = [];
			sources.forEach((source) => {
				let el = Markup.createMarkupEl(source);
				const headings = el.querySelectorAll('h2, h3');
				if (headings.length) {
					headings.forEach((heading) => titles.push(heading.textContent));
				}
			});
			if (titles.length) {
				postData.examples = titles;
			}
		}
		if (brief) {
			postData.brief = brief;
		}

		const data = await Fetch.post(`https://${window.location.hostname}/tools/api/generate-article-outline.php`, postData);
		return data;
	}

	async generateArticleSections(sectionTitles, sources = []) {
		let {topic, tone, customTone, options, relatedKeywords, toneLevel, dataSource, brief, pointOfView} = this.state;
		const {appSettings, focusKeyword} = this.props;

		const postData = {
			topic: topic || focusKeyword,
			pov: pointOfView,
			tone: tone === 'custom' ? customTone : tone,
			toneLevel: toneLevel,
			sectionTitles: sectionTitles,
			language: this.getCurrentLanguageName(),
			options: options,
			keyword: focusKeyword,
			settings: appSettings,
		};

		if ((dataSource === 'search' || dataSource === 'search-en' || dataSource === 'custom' || dataSource === 'serp-summary') && sources.length) {
			postData.sources = sources;
		}

		if (relatedKeywords) {
			postData.relatedKeywords = relatedKeywords.map((word) => word.name);
		}
		if (brief) {
			postData.brief = brief;
		}

		const sectionData = await Fetch.post(`https://${window.location.hostname}/tools/content/ajax/generate-article-sections.php`, postData);

		if (sectionData.status !== 'ok' || !sectionData.data?.sections?.length) {
			return [];
		}

		const sections = sectionData.data.sections.map((section) => {
			return {
				sectionTitle: section.sectionTitle,
				paragraphs: section.paragraphs.length ? section.paragraphs.map((p) => p.trim()).filter((p) => !!p) : [],
			}
		});

		return sections;

	}

	async generateArticleIntro(sectionTitles, sectionTexts) {
		const {topic, introLength, options, tone, toneLevel, customTone, pointOfView} = this.state;
		const {appSettings, focusKeyword} = this.props;

		const data = await Fetch.post(`https://${window.location.hostname}/tools/content/ajax/generate-article-intro.php`, {
			toneLevel: toneLevel,
			topic: topic || focusKeyword,
			introLength: introLength,
			headings: sectionTitles.map((title) => title.text),
			sectionTexts: sectionTexts,
			language: this.getCurrentLanguageName(),
			options: options,
			keyword: focusKeyword,
			settings: appSettings,
			pov: pointOfView,
			tone: tone === 'custom' ? customTone : tone,
		});
		return data;
	}

	async generateArticleTitle(sectionTitles) {
		const {topic, options} = this.state;
		const {focusKeyword, appSettings} = this.props;

		const data = await Fetch.post(`https://${window.location.hostname}/tools/content/ajax/generate-article-title.php`, {
			topic: topic || focusKeyword,
			headings: sectionTitles.map((title) => title.text),
			language: this.getCurrentLanguageName(),
			keyword: focusKeyword,
			options: options,
			settings: appSettings,
		});
		return data;
	}

	async generateArticleFAQ(sources = []) {
		const {topic, options, questionCount, questions, tone, customTone, toneLevel, dataSource, pointOfView, brief} = this.state;
		const {appSettings, focusKeyword} = this.props;

		const postData = {
			toneLevel: toneLevel,
			topic: topic || focusKeyword,
			questionCount: questionCount,
			questions: questions,
			language: this.getCurrentLanguageName(),
			options: options,
			keyword: focusKeyword,
			settings: appSettings,
			pov: pointOfView,
			tone: tone === 'custom' ? customTone : tone,
			brief: brief,
		};

		if ((dataSource === 'search' || dataSource === 'search-en' || dataSource === 'custom' || dataSource === 'serp-summary') && sources?.length) {
			postData.sources = sources;
		}

		const data = await Fetch.post(`https://${window.location.hostname}/tools/content/ajax/generate-article-faq.php`, postData);
		return data;
	}

	async generateArticleSummary(article) {
		const {options, tone, toneLevel, customTone, pointOfView} = this.state;
		const {appSettings, focusKeyword} = this.props;

		const data = await Fetch.post(`https://${window.location.hostname}/tools/content/ajax/generate-article-summary.php`, {
			toneLevel: toneLevel,
			language: this.getCurrentLanguageName(),
			options: options,
			article: article,
			keyword: focusKeyword,
			settings: appSettings,
			pov: pointOfView,
			tone: tone === 'custom' ? customTone : tone,
		});
		return data;
	}

	async generateKeyPoints(article) {
		const {options, tone, toneLevel, customTone, pointOfView} = this.state;
		const {appSettings} = this.props;
		const data = await Fetch.post(`https://${window.location.hostname}/tools/content/ajax/generate-key-points.php`, {
			tone: tone === 'custom' ? customTone : tone,
			toneLevel: toneLevel,
			language: this.getCurrentLanguageName(),
			options: options,
			article: article,
			settings: appSettings,
			pov: pointOfView,
		});
		return data;
	}

	async generateDidYouKnow(article, sources = []) {
		const {tone, customTone, toneLevel, pointOfView, dataSource} = this.state;
		const {appSettings, focusKeyword} = this.props;

		const postData = {
			tone: tone === 'custom' ? customTone : tone,
			toneLevel: toneLevel,
			subject: focusKeyword,
			language: this.getCurrentLanguageName(),
			article: article,
			settings: appSettings,
			pov: pointOfView,
		};

		if ((dataSource === 'search' || dataSource === 'search-en' || dataSource === 'custom' || dataSource === 'serp-summary') && sources?.length) {
			postData.sources = sources;
		}
		const data = await Fetch.post(`https://${window.location.hostname}/tools/content/ajax/generate-did-you-know.php`, postData);
		return data;
	}

	async generateCommentIncentive(article) {
		const {options, tone, toneLevel, customTone} = this.state;
		const {appSettings} = this.props;

		const data = await Fetch.post(`https://${window.location.hostname}/tools/content/ajax/generate-comment-incentive.php`, {
			tone: tone === 'custom' ? customTone : tone,
			toneLevel: toneLevel,
			language: this.getCurrentLanguageName(),
			options: options,
			article: article,
			settings: appSettings,
		});
		return data;
	}

	async humanizeText(text, isArticleSection) {
		const {options, tone, customTone, toneLevel, pointOfView} = this.state;
		const {appSettings} = this.props;

		const postData = {
			tone: tone === 'custom' ? customTone : tone,
			toneLevel: toneLevel,
			language: this.getCurrentLanguageName(),
			text: text,
			settings: appSettings,
			options: options,
			pov: pointOfView,
		};

		let data;
		if (isArticleSection) {
			data = await Fetch.post(`https://${window.location.hostname}/tools/content/ajax/humanize-article-section.php`, postData);
		} else {
			data = await Fetch.post(`https://${window.location.hostname}/tools/content/ajax/humanize-text.php`, postData);
		}
		return data;
	}

	getCurrentLanguageName() {
		let i = languages.findIndex((item) => item.langCode === this.props.language);
		let languageName = i > -1 ? languages[i].name : '';
		return languageName;
	}

	getCurrentLanguageNameTranslation() {
		let i = languages.findIndex((item) => item.langCode === this.props.language);
		let t = i > -1 ? languages[i].t : '';
		return window?.t[`LANG_${t}`];
	}

	async generateFailedParts() {
		const {failedParts, article, topic, headingCount, sources} = this.state;
		let {focusKeyword, relatedKeywords, passInfo} = this.props;
		var taskCount = failedParts.length;
		this.setState({
			taskCount: taskCount,
		});
		passInfo({
			loading: true,
			loadingTitle: window?.t?.WAIT,
			loadingTitleInfo: window?.t?.DONT_CLOSE_WINDOW,
			loadingInfo: window?.t?.RETRYING_FAILED_PARTS,
		});

		relatedKeywords = relatedKeywords.length ? relatedKeywords.map((word) => word.name) : [];
		const usedKeywords = [];

		if (focusKeyword && !relatedKeywords.length) {
			const optimalData = this.props.loadKeywordsAndOptimalValues();
			let keywords = optimalData.relatedKeywords;

			relatedKeywords = keywords.length ? keywords.map((word) => word.name) : [];
		}

		const {options} = this.state;

		for (let i = 0; i < failedParts.length; i ++) {
			switch (failedParts[i].type) {
				case 'outline':
					let outline = await this.generateArticleOutline(topic || focusKeyword, headingCount, sources);
					if (outline.status === 'ok') {
						article.sectionTitles = outline.outline;
						this.incTasksDone();
					}
					break;
				case 'sections':
					const sectionContent = await this.generateArticleSections(article.sectionTitles.map((item) => item.text), sources);

					if (sectionContent.length) {
						failedParts[i].type = '';
						this.setState({
							failedParts: failedParts,
						});

						for (let i = 0; i < article.sectionTitles.length; i ++) {
							let index = sectionContent.findIndex((section) => section.sectionTitle === article.sectionTitles[i].text);
							if (index > -1) {
								article.sectionTexts[i] = sectionContent[index].paragraphs;
								if (options.includeRelatedKeywords) {
									relatedKeywords = relatedKeywords.filter((word) => {
										for (var j = 0; j < sectionContent[index].paragraphs.length; j ++) {
											if (sectionContent[index].paragraphs[j] && sectionContent[index].paragraphs[j].includes(word)) {
												usedKeywords.push(word);
												return false;
											}
										}
										return true;
									});
								}
								if (options.fixParagraphs) {
									passInfo({
										loading: `${window?.t?.FIXING_CONTENT_FOR_SECTION}: ${article.sectionTitles[i].text}...`,
									});
									let pCount = article.sectionTexts[i].length;
									for (let j = 0; j < pCount; j ++) {
										article.sectionTexts[i][j] = await this.fixParagraph(article.sectionTexts[i][j]);
									}
								}
								if (options.proofread) {
									passInfo({
										loading: `${window?.t?.PROOFREADING_SECTION}: ${article.sectionTitles[i].text}...`,
									});
									let pCount = article.sectionTexts[i].length;
									for (let j = 0; j < pCount; j ++) {
										article.sectionTexts[i][j] = await this.proofread(article.sectionTexts[i][j]);
									}
								}
							}
						}
						this.incTasksDone();
					}
					break;
				case 'title':
					let title = await this.generateArticleTitle(article.sectionTitles);
					if (title.status === 'ok') {
						failedParts[i].type = '';
						article.title = title.text;
						this.incTasksDone();
					}
					break;
				case 'intro':
					let intro = await this.generateArticleIntro(article.sectionTitles);
					if (intro.status === 'ok') {
						failedParts[i].type = '';
						article.intro = intro.text;
						this.incTasksDone();
					}
					break;
				case 'faq':
					let faq = await this.generateArticleFAQ(sources);
					if (faq.status === 'ok') {
						failedParts[i].type = '';
						article.faqTitle = faq.title;
						article.faqQuestions = faq.questions;
						this.incTasksDone();
					}
					break;
				case 'summary':
					let summary = await this.generateArticleSummary(article);
					if (summary.status === 'ok') {
						failedParts[i].type = '';
						article.summaryTitle = summary.title;
						article.summary = summary.text;
						this.incTasksDone();
					}
					break;
				case 'keyPoints':
					let keyPoints = await this.generateKeyPoints(article);
					if (keyPoints.status === 'ok') {
						failedParts[i].type = '';
						article.keyPoints = keyPoints.points.filter((point) => {
							let text = Markup.stripTags(point).trim();
							return !!text;
						});
						this.incTasksDone();
					}
					break;
				case 'didYouKnow':
					let didYouKnow = await this.generateDidYouKnow(article);
					if (didYouKnow.status === 'ok') {
						failedParts[i].type = '';
						article.didYouKnow = didYouKnow.content;
						this.incTasksDone();
					}
					break;
				case 'commentIncentive':
					let commentInc = await this.generateCommentIncentive(article);
					if (commentInc.status === 'ok') {
						failedParts[i].type = '';
						article.commentIncentive = commentInc.text;
						this.incTasksDone();
					}
					break;
				default:
					console.log('Unknown article part');
					break;
			}
		}
		this.setState({
			failedParts: failedParts.filter((part) => !!part.type),
			article: article,
			taskCount: 0,
		});
		passInfo({
			loading: false,
			loadingTitle: '',
			loadingInfo: '',
			loadingProgress: 0,
			loaderType: '',
		});

		let articleMarkup = this.buildArticleMarkup(article);
		this.props.contentCallback(articleMarkup);
	}

	buildArticleMarkup(parts) {
		const markup = document.createElement('div');
		if (parts.title) {
			markup.append(this.makeElement('h1', parts.title.trim().replaceAll('"', '')));
		}
		if (parts.intro) {
			markup.append(this.makeElement('p', parts.intro.trim()));
		}
		if (parts.keyPoints) {
			const keyPointListItems = parts.keyPoints.map((point) => `<li>${point}</li>`);
			const keyPointList = this.makeElement('ul', keyPointListItems.join(''));
			markup.append(keyPointList);
		}
		if (parts.sectionTitles.length) {
			parts.sectionTitles.forEach((sectionTitle, index) => {
				let headingEl = 'h' + (parseInt(sectionTitle.level) + 1);
				markup.append(this.makeElement(headingEl, sectionTitle.text.trim()));
				const sectionContent = parts.sectionTexts[index] || '';
				if (sectionContent.length) {
					sectionContent.forEach((text) => {
						if (text) {
							markup.append(this.makeElement('p', text.trim()));
						}
					});
				}
			});
		}

		if (parts.didYouKnow) {
			markup.append(this.makeElement('h2', parts.didYouKnow.title));
			markup.append(this.makeElement('p', parts.didYouKnow.text));
		}
		if (parts.faqQuestions) {
			if (parts.faqTitle) {
				markup.append(this.makeElement('h2', parts.faqTitle.replaceAll('TITLE: ', '').trim()));
			}
			parts.faqQuestions.forEach((q) => {
				markup.append(this.makeElement('h3', q.q));
				markup.append(this.makeElement('p', q.a));
			});
		}
		if (parts.summaryTitle) {
			markup.append(this.makeElement('h2', parts.summaryTitle.trim()));
		}
		if (parts.summary) {
			markup.append(this.makeElement('p', parts.summary.trim()));
		}
		if (parts.commentIncentive) {
			markup.append(this.makeElement('p', `<em>${parts.commentIncentive}</em>`));
		}

		if (this.state.options.boldForKeywords) {

			let phrasesInStrong = [];
			const strong = markup.querySelectorAll('strong');
			if (strong.length) {
				strong.forEach((el) => phrasesInStrong.push(el.textContent));
			}
			phrasesInStrong = phrasesInStrong.filter(this.uniqueValues);

			if (phrasesInStrong.length) {
				let articleText = markup.innerHTML;
				phrasesInStrong.forEach((phrase) => {
					let first = true;
					let regex = new RegExp(`<strong>${phrase}</strong>`, 'gi');
					articleText = articleText.replace(regex, function(match) {
						let result = first ? match : match.replace('<strong>', '').replace('</strong>', '');
						if (first) {
							first = false;
						}
						return result;
					});
				});
				markup.innerHTML = articleText;
			}
		}
		return markup.innerHTML;
	}

	makeElement(tagName, text) {
		const el = document.createElement(tagName);
		el.innerHTML = text;
		return el;
	}

	incTasksDone() {
		let done = this.state.tasksDone + 1;

		this.setState({
			tasksDone: done,
		});
		this.props.passInfo({
			loadingProgress: Math.round(done / this.state.taskCount * 100),
		});
	}

	async detectAI(text) {
		const postData = {
			text: text,
		};
		if (this.state.zerogptToken) {
			postData.token = this.state.zerogptToken;
		}

		let result = await Fetch.post(`https://${window.location.hostname}/tools/content/ajax/detect-ai.php`, postData);
		if (result.token) {
			this.setState({
				zerogptToken: result.token
			});
		}
		return result;
	}

	async avoidAIDetection(text, isArticleSection = false) {
		let original = text;
		text = Markup.stripTags(text);
		let aiDetectionResult = await this.detectAI(text);

		if (aiDetectionResult.data.isHuman < 80) {
			const humanizingResult = await this.humanizeText(text, isArticleSection);
			if (humanizingResult.status === 'ok') {
				return humanizingResult.p || humanizingResult.text;
			}
		}

		return isArticleSection ? false : original;
	}

	async proofread(text) {
		const {appSettings, focusKeyword} = this.props;
		const {options, tone, customTone, toneLevel} = this.state;

		let result = await Fetch.post(`https://${window.location.hostname}/tools/content/ajax/proofread.php`, {
			tone: tone === 'custom' ? customTone : tone,
			toneLevel: toneLevel,
			text: text,
			settings: appSettings,
			options: options,
			keyword: focusKeyword,
			language: this.getCurrentLanguageName(),
		});

		return result.status === 'ok' ? result.text : text;
	}

	async fixParagraph(text) {
		const {options} = this.state;
		const {focusKeyword, appSettings} = this.props;

		let result = await Fetch.post(`https://${window.location.hostname}/tools/content/ajax/fix-paragraph.php`, {
			text: text,
			language: this.getCurrentLanguageName(),
			options: options,
			keyword: focusKeyword,
			settings: appSettings,
		});

		return result.status === 'ok' ? result.text : text;
	}

	estimateArticleLength() {
		const {appSettings} = this.props;
		const {options, questionCount, introLength} = this.state;

		let {headingCount} = this.state;
		headingCount = parseInt(headingCount);

		let paragraphCount = headingCount * appSettings.maxParagraphsPerHeading;
		if (options.generateKeyPoints) {
			headingCount *= 2;
		}
		if (options.generateFAQ) {
			paragraphCount += parseInt(questionCount);
			headingCount += parseInt(questionCount);
		}
		if (options.generateArticleSummary) {
			headingCount ++;
			paragraphCount ++;
		}
		if (options.generateCommentIncentive) {
			paragraphCount ++;
		}
		if (options.generateTitle) {
			headingCount ++;
		}

		let wordCount = appSettings.maxWordsPerParagraph * paragraphCount * 0.6;
		wordCount += headingCount * 6;
		if (options.generateArticleIntro) {
			wordCount += introLength;
		}

		return wordCount;
	}

	async checkOpenAiKey() {
		const data = await Fetch.post(`https://${window.location.hostname}/tools/content/ajax/openai-key.php`, {
			action: 'check',
		});
		return data.status === 'ok' ? data.apiKeyOK : false;
	}

	checkPermission(name) {
		return this.props.permissions && this.props.permissions.includes(name);
	}

	uniqueValues(value, index, array) {
		return array.indexOf(value) === index;
	}
}

export default ArticleGenerator;
